import createDOMPurify, { type Config, type DOMPurify } from "dompurify";

/**
 * Everything the Help pages render from the support API that is not plain text goes through here.
 *
 * The server sanitizes bodies on the way in, but the browser does not take that on trust: a row
 * written before the sanitizer existed, a code path that skipped it, or a hostile response would
 * otherwise execute in the reader's session. Defence in depth, so both ends hold the line.
 */

/** A ticket message: the composer's formatting, nothing more. */
const MESSAGE_CONFIG: Config = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "b",
    "strong",
    "i",
    "em",
    "u",
    "ul",
    "ol",
    "li",
    "a",
    "blockquote",
    "code",
    "pre",
  ],
  ALLOWED_ATTR: ["href", "target", "rel"],
  ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

/** A knowledge-base article: mirrors the server's KbHtmlSanitizer allowlist. */
const ARTICLE_CONFIG: Config = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "hr",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "code",
    "pre",
    "blockquote",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "th",
    "td",
    "a",
    "img",
    "video",
    "source",
    "figure",
    "figcaption",
    "span",
    "div",
  ],
  ALLOWED_ATTR: [
    "id",
    "class",
    "title",
    "dir",
    "lang",
    "href",
    "target",
    "rel",
    "src",
    "alt",
    "width",
    "height",
    "loading",
    "poster",
    "controls",
    "type",
    "colspan",
    "rowspan",
    "scope",
    "start",
  ],
  // http(s), mailto, or a relative reference (no scheme at all).
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

const NEW_TAB_REL = "noopener noreferrer";

let messagePurifier: DOMPurify | undefined;
let articlePurifier: DOMPurify | undefined;

/** Its own instance, so the link hook never leaks into another caller's DOMPurify. */
function purifier(newTab: (anchor: Element) => boolean): DOMPurify {
  const purify = createDOMPurify(window);
  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.nodeName !== "A") return;
    if (newTab(node)) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", NEW_TAB_REL);
    } else {
      node.removeAttribute("target");
    }
  });
  return purify;
}

/** Fails closed: where DOMPurify cannot run, nothing is rendered rather than the raw string. */
function run(purify: DOMPurify, html: string | null | undefined, config: Config): string {
  if (!purify.isSupported || !html) return "";
  return purify.sanitize(html, { ...config, RETURN_TRUSTED_TYPE: false }) as string;
}

/** A ticket message or internal note, safe to set as innerHTML. Links open in a new tab. */
export function sanitizeMessageHtml(html: string | null | undefined): string {
  messagePurifier ??= purifier((a) => a.hasAttribute("href"));
  return run(messagePurifier, html, MESSAGE_CONFIG);
}

/** An article body, safe to set as innerHTML. A link the author sent to a new tab gets `rel`. */
export function sanitizeArticleHtml(html: string | null | undefined): string {
  articlePurifier ??= purifier((a) => a.hasAttribute("target"));
  return run(articlePurifier, html, ARTICLE_CONFIG);
}
